import { ComponentPrs } from '../../api/MergeOpsApi';
import { DeployState, DeployStatus } from '../../api/types';

/** Deploy flow target: a single env, or both at once (env/all deploy PRs). */
export type DeployTarget = 'stg' | 'prd' | 'all';

/** Concrete Cloud Run envs a target deploys to (and must all succeed). */
export type DeployRunEnv = 'stg' | 'prd';

export type DeployRunPhase =
  | 'merging' // merging the current wave's PRs
  | 'polling' // watching Cloud Run for the current wave to deploy
  | 'done' // every wave succeeded
  | 'failed' // a wave's deploy failed; the run stopped
  | 'timeout' // a wave did not finish within the timeout; the run stopped
  | 'cancelled'; // user cancelled; the in-flight wave finished, later waves never merged

export interface DeployRunComponent {
  componentName: string;
  projectName: string;
  /** Deploy PR numbers merged for this component in this wave. */
  prNumbers: number[];
  /**
   * Envs this component does NOT deploy to (`dummy/` placeholder manifest,
   * no deploy_kit trigger). Gating and polling skip these (component, env)
   * pairs — otherwise the wave waits for a build that never comes until the
   * 1h timeout.
   */
  dummyEnvs?: DeployRunEnv[];
}

/** The envs a component actually deploys to for this run (dummy envs skipped). */
export function componentEnvs(
  component: DeployRunComponent,
  envs: DeployRunEnv[],
): DeployRunEnv[] {
  return envs.filter(env => !component.dummyEnvs?.includes(env));
}

/** Whether a human skipped this (component, env) in the current run. */
export function isDeploySkipped(
  skipped: DeploySkip[] | undefined,
  componentName: string,
  env: DeployRunEnv,
): boolean {
  return (
    skipped?.some(s => s.componentName === componentName && s.env === env) ??
    false
  );
}

/**
 * The envs of a component this run still waits on: `componentEnvs` (which drops
 * `dummy/` placeholders) minus the ones a human skipped. Gating and polling both
 * go through here, so a skip takes effect in one place.
 */
export function activeEnvs(
  component: DeployRunComponent,
  envs: DeployRunEnv[],
  skipped?: DeploySkip[],
): DeployRunEnv[] {
  return componentEnvs(component, envs).filter(
    env => !isDeploySkipped(skipped, component.componentName, env),
  );
}

export interface DeployRunWave {
  wave: number;
  components: DeployRunComponent[];
}

/**
 * A (component, env) a human took out of this run's gating: its deploy failed
 * or was stuck, they judged it handled (or not worth waiting for), and the run
 * moves on without it. `state` records what it looked like at that moment, so
 * a deliberate cut-off is later distinguishable from a settled failure.
 */
export interface DeploySkip {
  componentName: string;
  env: DeployRunEnv;
  state: DeployState;
  reason: string;
  /** ISO time the skip was recorded. */
  at: string;
}

/**
 * A wave-by-wave deploy run: merge wave N, wait until every component's Cloud
 * Run deploy succeeds, then auto-advance to wave N+1. A failure or timeout in
 * any wave stops the run so the later waves never merge.
 */
export interface DeployRun {
  target: DeployTarget;
  waves: DeployRunWave[];
  currentWaveIndex: number;
  /** ISO baseline for the current wave: only revisions created at/after it count. */
  since: string;
  /** Wall-clock start of the current wave, for the per-wave timeout. */
  waveStartedMs: number;
  phase: DeployRunPhase;
  /** (component, env) pairs a human took out of gating, with why. */
  skipped?: DeploySkip[];
}

export function envsForTarget(target: DeployTarget): DeployRunEnv[] {
  if (target === 'all') return ['stg', 'prd'];
  return [target];
}

/** Phases a stopped run can be resumed from (`done` is finished, not stopped). */
export function canResumeDeployRun(phase: DeployRunPhase): boolean {
  return phase === 'failed' || phase === 'timeout' || phase === 'cancelled';
}

/**
 * A stopped run set back to watching its current wave: same waves and `since`,
 * fresh timeout window. The poll driver then takes over — deploys that
 * succeeded meanwhile (e.g. a manually re-run Cloud Build) advance the run,
 * and PRs a mid-wave stop left unmerged are merged by the normal advance
 * (merging is idempotent for already-merged PRs, whose deploys are found via
 * their original merge time). Null when the run isn't in a resumable phase.
 */
export function resumedDeployRun(
  run: DeployRun,
  nowMs: number,
): DeployRun | null {
  if (!canResumeDeployRun(run.phase)) return null;
  return { ...run, phase: 'polling', waveStartedMs: nowMs };
}

/**
 * The wave's gate entries whose last polled state is not yet terminal —
 * builds that may still be running (or still fire) in Cloud Build. What a
 * STOPPED run keeps watching: stopping the gate must not stop observing
 * deploys the run already triggered.
 */
export function inFlightWaveEntries(
  wave: DeployRunWave,
  envs: DeployRunEnv[],
  statuses: Record<string, DeployStatus>,
  skipped?: DeploySkip[],
): { componentName: string; env: DeployRunEnv }[] {
  return waveProbes(wave, envs, skipped).filter(
    probe =>
      !isTerminalDeploy(
        statuses[deployStatusKey(probe.componentName, probe.env)]?.state ??
          'none',
      ),
  );
}

/** Status map key for a (component, env) pair. */
export function deployStatusKey(
  componentName: string,
  env: DeployRunEnv,
): string {
  return `${componentName}:${env}`;
}

/** A deploy state past which we stop polling that (component, env). */
export function isTerminalDeploy(state: DeployState): boolean {
  return state === 'success' || state === 'failure' || state === 'timeout';
}

/** Status-dot category for a deploy state (mirrors buildDotKind). */
export function deployDotKind(
  state: DeployState,
): 'running' | 'done' | 'failed' | 'idle' {
  if (state === 'deploying') return 'running';
  if (state === 'success') return 'done';
  if (state === 'failure' || state === 'timeout') return 'failed';
  return 'idle';
}

export function deployStateLabel(state: DeployState): string {
  switch (state) {
    case 'none':
      return 'waiting for deploy';
    case 'deploying':
      return 'deploying';
    case 'success':
      return 'success';
    case 'failure':
      return 'failed';
    case 'timeout':
      return 'timed out';
    default:
      return 'unknown';
  }
}

/**
 * Snapshot the deploy PRs of each wave for `target` into a run plan. Sections
 * are the per-wave deploy sections already filtered to the target; an empty
 * wave (no PRs) is skipped so it neither merges nor blocks.
 */
export function buildDeployRunWaves(
  sections: { wave?: number; components: ComponentPrs[] }[],
  _target: DeployTarget,
): DeployRunWave[] {
  const waves: DeployRunWave[] = [];
  for (const section of sections) {
    if (section.wave === undefined) continue;
    const components = section.components
      .map(c => ({
        componentName: c.componentName,
        projectName: c.projectName,
        prNumbers: c.items.map(pr => pr.number),
        dummyEnvs: (c.currentTags?.dummyEnvs ?? []).filter(
          (env): env is DeployRunEnv => env === 'stg' || env === 'prd',
        ),
      }))
      .filter(c => c.prNumbers.length > 0);
    if (components.length > 0) {
      waves.push({ wave: section.wave, components });
    }
  }
  return waves;
}

export type WaveMergeResult =
  | { outcome: 'merged'; since?: string }
  | { outcome: 'cancelled'; mergedCount: number }
  | { outcome: 'failed'; error: unknown };

/** The earlier of two ISO timestamps; null/invalid values are ignored. */
export function minIso(a: string | null, b: string | null): string | null {
  const aMs = a === null ? NaN : Date.parse(a);
  const bMs = b === null ? NaN : Date.parse(b);
  if (Number.isNaN(aMs)) return Number.isNaN(bMs) ? null : b;
  if (Number.isNaN(bMs)) return a;
  return aMs <= bMs ? a : b;
}

/**
 * Merge one wave's deploy PRs serially, so a mid-wave failure stops the rest.
 * `shouldCancel` is consulted before every single merge: a cancel that lands
 * while the wave is still merging must not merge the PRs it hasn't reached yet
 * (the ones already merged can't be taken back — their deploys have started).
 *
 * A merged outcome carries `since`: the earliest server-reported merge time
 * across the wave (an already-merged PR reports its original merge time), so
 * the caller's Cloud Build `since` filter covers every deploy this wave
 * triggered — including ones from a partially merged earlier attempt — without
 * trusting the browser clock.
 */
export async function mergeWavePrs(
  wave: DeployRunWave,
  deps: {
    mergePr: (
      componentName: string,
      prNumber: number,
    ) => Promise<{ mergedAt?: string | null } | void>;
    onMerged: (componentName: string, prNumber: number) => void;
    shouldCancel: () => boolean;
  },
): Promise<WaveMergeResult> {
  let mergedCount = 0;
  let earliestMergedAt: string | null = null;
  for (const component of wave.components) {
    for (const prNumber of component.prNumbers) {
      if (deps.shouldCancel()) return { outcome: 'cancelled', mergedCount };
      try {
        const result = await deps.mergePr(component.componentName, prNumber);
        earliestMergedAt = minIso(earliestMergedAt, result?.mergedAt ?? null);
      } catch (error) {
        return { outcome: 'failed', error };
      }
      deps.onMerged(component.componentName, prNumber);
      mergedCount += 1;
    }
  }
  return { outcome: 'merged', since: earliestMergedAt ?? undefined };
}

export type WaveOutcome = 'success' | 'failed' | 'pending';

/**
 * Outcome of the current wave from the polled statuses. A wave succeeds only
 * when every (component, env) reaches `success`; it fails as soon as any one
 * fails; once the timeout elapses, any not-yet-terminal entry counts as failed.
 */
export function waveOutcome(
  wave: DeployRunWave,
  envs: DeployRunEnv[],
  statuses: Record<string, DeployStatus>,
  timedOut: boolean,
  skipped?: DeploySkip[],
): WaveOutcome {
  let allSuccess = true;
  for (const component of wave.components) {
    for (const env of activeEnvs(component, envs, skipped)) {
      const status = statuses[deployStatusKey(component.componentName, env)];
      const state = status?.state ?? 'none';
      // `timeout` is terminal too (the Cloud Build itself timed out) — without
      // this the wave keeps polling until the run's 1h limit even though the
      // outcome is already known.
      if (state === 'failure' || state === 'timeout') return 'failed';
      if (state !== 'success') {
        if (timedOut) return 'failed';
        allSuccess = false;
      }
    }
  }
  return allSuccess ? 'success' : 'pending';
}

/** Format a millisecond duration as `HH:MM:SS` (e.g. 632000 → "00:10:32"). */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

/**
 * Wall-clock `HH:MM:SS` for a build timestamp, or null when there is none to
 * show. Paired with the elapsed counter so a row says *when* the trigger fired,
 * not just how long it has been running — a tfez apply build surfaces ~95s
 * after the merge, and only the absolute time distinguishes "triggered, still
 * queued" from "never triggered".
 */
export function formatClockTime(
  iso: string | null | undefined,
  timeZone?: string,
): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(ms);
}

/**
 * Elapsed time (ms) to show for a row, or null when it hasn't started yet
 * (a future/queued wave). Counts from the deploy build's startTime once it
 * exists; before that, from the wave's merge (waveStartedMs) for the current
 * wave so a long "waiting for deploy" still visibly ticks. Freezes at the
 * build's finishTime once the deploy is terminal.
 */
export function rowElapsedMs(
  status: DeployStatus | undefined,
  waveStartedMs: number,
  isCurrentWave: boolean,
  nowMs: number,
): number | null {
  let start: number;
  if (status?.startTime) {
    start = Date.parse(status.startTime);
  } else if (isCurrentWave) {
    start = waveStartedMs;
  } else {
    return null;
  }
  const end = status?.finishTime ? Date.parse(status.finishTime) : nowMs;
  return end - start;
}

/**
 * The wave's gate entries that are neither success nor skipped, per the last
 * polled statuses. What the skip dialog uses to say how many OTHER entries
 * still block the wave — the wave advances once every entry is success or
 * skipped.
 */
export function unresolvedWaveEntries(
  wave: DeployRunWave,
  envs: DeployRunEnv[],
  statuses: Record<string, DeployStatus>,
  skipped?: DeploySkip[],
): { componentName: string; env: DeployRunEnv }[] {
  return waveProbes(wave, envs, skipped).filter(
    probe =>
      (statuses[deployStatusKey(probe.componentName, probe.env)]?.state ??
        'none') !== 'success',
  );
}

/** (component, env) pairs to poll for a wave (dummy and skipped envs excluded). */
export function waveProbes(
  wave: DeployRunWave,
  envs: DeployRunEnv[],
  skipped?: DeploySkip[],
): { componentName: string; env: DeployRunEnv }[] {
  return wave.components.flatMap(component =>
    activeEnvs(component, envs, skipped).map(env => ({
      componentName: component.componentName,
      env,
    })),
  );
}
